import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardTitle, Grid, MetricCard, Pill, Btn, Insight, EmptyState } from '../components/UI';
import { supabase } from '../supabaseClient';

// Visa rules — the human gate for live visa updates (27 Sep 2026).
// Claude proposes, this screen publishes. See api/visa-review.js.

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const ENTRY_LABEL = {
  'visa-free': 'Visa-free', eta: 'ETA first', 'on-arrival': 'On arrival',
  'e-visa': 'e-Visa', embassy: 'Embassy visa',
};
const ENTRY_TYPE = { 'visa-free': 'green', eta: 'teal', 'on-arrival': 'teal', 'e-visa': 'amber', embassy: 'red' };

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function rule(entry, days) {
  if (!entry) return <span style={{ color: '#9aa7ae' }}>not verified</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <Pill type={ENTRY_TYPE[entry] || 'blue'}>{ENTRY_LABEL[entry] || entry}</Pill>
      <span style={{ fontSize: 12.5, color: '#2f4552' }}>{days != null ? days + ' d' : '—'}</span>
    </span>
  );
}

export default function VisaRules() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [skip, setSkip] = useState({});   // rule id -> true: left out of this group's action
  const [notes, setNotes] = useState({}); // dest -> note
  const [recheck, setRecheck] = useState('');
  const [msg, setMsg] = useState('');

  const authHeaders = useCallback(async () => {
    const { data: s } = await supabase.auth.getSession();
    const token = s && s.session && s.session.access_token;
    return token ? { Authorization: 'Bearer ' + token } : {};
  }, []);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch('/api/visa-review', { headers: await authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'load failed');
      setData(j);
    } catch (e) {
      setErr(e.message);
      setData({ pending: [], runs: [], sources: [], published: 0 });
    }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  async function post(body, key) {
    if (busy) return null;
    setBusy(key); setErr(''); setMsg('');
    try {
      const r = await fetch('/api/visa-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'action failed');
      return j;
    } catch (e) { setErr(e.message); return null; } finally { setBusy(null); }
  }

  const names = useMemo(() => {
    const m = {};
    for (const s of (data && data.sources) || []) m[s.dest_iso3] = s.dest_name;
    return m;
  }, [data]);

  const groups = useMemo(() => {
    const g = {};
    for (const p of (data && data.pending) || []) (g[p.dest_iso3] || (g[p.dest_iso3] = [])).push(p);
    return Object.entries(g);
  }, [data]);

  async function decide(dest, rows, action) {
    const ids = rows.filter((r) => !skip[r.id]).map((r) => r.id);
    const j = await post({ action, ids, note: notes[dest] || '' }, dest + action);
    if (j) { setMsg(`${names[dest] || dest}: ${j.changed} ${action === 'publish' ? 'published' : 'rejected'}.`); load(); }
  }

  const pending = data ? data.pending.length : null;
  const watched = data ? data.sources.length : null;
  const flagged = data ? data.sources.filter((s) => s.needs_research).length : null;
  const lastWatch = data && data.runs.find((r) => r.kind === 'watch');
  const research = data ? data.runs.filter((r) => r.kind === 'research') : [];

  return (
    <div style={{ fontFamily: F }}>
      <Grid cols={4}>
        <MetricCard label="Waiting for you" value={pending === null ? '…' : String(pending)} sub="proposed rule changes" accent="#314F5C" />
        <MetricCard label="Live in the app" value={data ? String(data.published) : '…'} sub="published changes" accent="#1aa36b" />
        <MetricCard label="Queued for Claude" value={flagged === null ? '…' : String(flagged)} sub={`of ${watched ?? '…'} destinations watched`} accent="#9F7E4F" />
        <MetricCard label="Last page check" value={lastWatch ? when(lastWatch.started_at) : '—'} sub={lastWatch ? (lastWatch.note || '') : 'not run yet'} accent="#6F8FA7" />
      </Grid>

      <Insight type="info">
        <b>Nothing here is shown to travellers until you publish it.</b> Every two hours the official pages
        behind each destination are checked; a page that changes and stays changed is sent to Claude, which
        re-reads the rules and proposes only what differs. <b>Publish</b> puts the rule on phones within seconds, no app release needed. <b>Reject</b> needs a note. Untick a row to leave it out.
      </Insight>

      {err ? <Insight type="warn">{err}</Insight> : null}
      {msg ? <Insight type="ok">{msg}</Insight> : null}

      <Card>
        <CardTitle action={<Btn variant="outline" small onClick={load}>Refresh</Btn>}>Proposed changes</CardTitle>
        {data === null ? (
          <div style={{ padding: 18, color: '#6b7a83', fontSize: 13 }}>Loading…</div>
        ) : groups.length === 0 ? (
          <EmptyState title="Nothing to review" body="Proposed changes appear here after Claude re-checks a destination." icon="✓" />
        ) : groups.map(([dest, rows]) => (
          <div key={dest} style={{ borderTop: '1px solid #eef2f5', padding: '18px 2px' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#16303f' }}>{names[dest] || dest}</span>
              <Pill type="teal">{rows.length} change{rows.length === 1 ? '' : 's'}</Pill>
              <span style={{ fontSize: 12, color: '#7b8b94' }}>proposed {when(rows[0].created_at)} · {rows[0].proposed_by}</span>
            </div>
            {rows[0].reason ? <div style={{ fontSize: 13, color: '#2f4552', marginTop: 8, lineHeight: 1.6 }}>{rows[0].reason}</div> : null}
            <div style={{ overflowX: 'auto', marginTop: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#7b8b94', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                    <th style={{ padding: 6 }} />
                    <th style={{ padding: 6 }}>Passport</th>
                    <th style={{ padding: 6 }}>App says now</th>
                    <th style={{ padding: 6 }}>Proposed</th>
                    <th style={{ padding: 6 }}>Note · source</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid #f1f4f6', opacity: skip[r.id] ? 0.45 : 1 }}>
                      <td style={{ padding: 6 }}>
                        <input type="checkbox" checked={!skip[r.id]} onChange={() => setSkip((s) => ({ ...s, [r.id]: !s[r.id] }))} />
                      </td>
                      <td style={{ padding: 6, fontWeight: 600, color: '#16303f' }}>{names[r.passport_iso3] || r.passport_iso3}</td>
                      <td style={{ padding: 6 }}>{rule(r.prev_entry, r.prev_stay_days)}</td>
                      <td style={{ padding: 6 }}>{rule(r.entry, r.stay_days)}</td>
                      <td style={{ padding: 6, color: '#2f4552', lineHeight: 1.5 }}>
                        {r.note ? <div>{r.note}</div> : null}
                        <a href={r.source_url} target="_blank" rel="noreferrer" style={{ color: '#314F5C', fontWeight: 600, wordBreak: 'break-all' }}>{r.source_url} ↗</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
              <input
                value={notes[dest] || ''}
                onChange={(e) => setNotes((n) => ({ ...n, [dest]: e.target.value }))}
                placeholder="Note for the record (optional; required to reject)"
                style={{ flex: 1, minWidth: 180, padding: '9px 12px', border: '1px solid #d7dee3', borderRadius: 8, fontSize: 13 }}
              />
              <Btn small onClick={() => decide(dest, rows, 'publish')} disabled={!!busy}>
                Publish {rows.filter((r) => !skip[r.id]).length}
              </Btn>
              <Btn small variant="danger" onClick={() => decide(dest, rows, 'reject')} disabled={!!busy}>Reject</Btn>
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <CardTitle>Ask Claude to re-check a destination</CardTitle>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '4px 2px 8px' }}>
          <select value={recheck} onChange={(e) => setRecheck(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #d7dee3', borderRadius: 8, fontSize: 13, minWidth: 220 }}>
            <option value="">Choose a destination…</option>
            {((data && data.sources) || []).map((s) => (
              <option key={s.dest_iso3} value={s.dest_iso3}>{s.dest_name}{s.needs_research ? ' (queued)' : ''}</option>
            ))}
          </select>
          <Btn small disabled={!recheck || !!busy} onClick={async () => {
            const j = await post({ action: 'recheck', dest: recheck }, 'recheck');
            if (j) { setMsg(`${names[recheck] || recheck} queued — Claude re-checks one destination every three hours.`); load(); }
          }}>Queue re-check</Btn>
        </div>
      </Card>

      <Card>
        <CardTitle>Claude runs</CardTitle>
        {research.length === 0 ? (
          <div style={{ padding: 14, color: '#6b7a83', fontSize: 13 }}>No research runs yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#7b8b94', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                  <th style={{ padding: 6 }}>When</th><th style={{ padding: 6 }}>Destination</th><th style={{ padding: 6 }}>Proposed</th>
                  <th style={{ padding: 6 }}>Tokens in / out</th><th style={{ padding: 6 }}>Searches</th><th style={{ padding: 6 }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {research.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #f1f4f6' }}>
                    <td style={{ padding: 6, whiteSpace: 'nowrap' }}>{when(r.started_at)}</td>
                    <td style={{ padding: 6 }}>{names[r.dest_iso3] || r.dest_iso3}</td>
                    <td style={{ padding: 6 }}>{r.proposed}</td>
                    <td style={{ padding: 6 }}>{r.input_tokens != null ? `${r.input_tokens.toLocaleString()} / ${(r.output_tokens || 0).toLocaleString()}` : '—'}</td>
                    <td style={{ padding: 6 }}>{r.web_searches ?? '—'}</td>
                    <td style={{ padding: 6, color: '#2f4552' }}>{r.note || (r.finished_at ? '' : 'running…')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
